/**
 * Mixpanel Event Tracking Utilities
 *
 * This module provides functions for tracking custom events in Mixpanel.
 * All functions safely check if Mixpanel is loaded before tracking.
 */

/**
 * Safely track a Mixpanel event
 * @param {string} eventName - The name of the event
 * @param {object} properties - Event properties
 */
type AppletOwnerType = "user" | "service"
type AppletPublishedType = "published" | "diy"
type AppletConfigType = "static" | "dynamic"
type AuthType = "oauth" | "pin" | "token" | "modular"
type ConnectionContext = "trigger" | "action" | "query" | "manual"
type PlanInterval = "month" | "year"
type SubscriptionPricingModel = "monthly" | "annual"
type SubscriptionName = "pro" | "pro_plus" | "free"
type PaymentMethodType = "applePay" | "googlePay" | "link" | "card"

const trackEvent = (eventName: string, properties: Record<string, unknown> = {}) => {
  if (window.App?.user?.id) {
    const user_traits_snapshot = {
      plan: (window.App.user.tier && toSubscriptionName(window.App.user.tier)) || "free",
      eligible_for_trial: window.App.user.eligible_for_trial,
    }
    if (window.App.user.payment_interval) {
      user_traits_snapshot.payment_interval = toPricingModel(window.App.user.payment_interval)
    }
    if (window.App.user.payment_platform) {
      user_traits_snapshot.payment_platform = window.App.user.payment_platform
    }
    Object.assign(properties, { user_traits_snapshot })
  }
  window?.mixpanel?.track?.(eventName, properties)
}

// ============================================================================
// APPLET LIFECYCLE EVENTS
// ============================================================================

interface TrackAppletConnectionStartedPayload {
  appletId: string
  appletOwnerType: AppletOwnerType
  appletPublishedType: AppletPublishedType
  appletConfigType: AppletConfigType
  appletAuthor: string
  triggerService: string
  actionServices: string[]
  appletTags: string[]
}
export const trackAppletConnectionStarted = ({
  appletId,
  appletOwnerType,
  appletPublishedType,
  appletConfigType,
  appletAuthor,
  triggerService,
  actionServices,
  appletTags,
}: TrackAppletConnectionStartedPayload) => {
  trackEvent("Applet Connection Started", {
    applet_slug: appletId,
    applet_owner_type: appletOwnerType, // "user" | "service"
    applet_published_type: appletPublishedType, // "published" | "diy"
    applet_config_type: appletConfigType, // "static" | "dynamic"
    applet_author: appletAuthor,
    trigger_service: triggerService,
    action_services: actionServices,
    applet_tags: appletTags,
  })
}

interface TrackAppletConnectionCompletedPayload extends TrackAppletConnectionStartedPayload {
  appletStatus: string
  appletTier: string
  queryCount?: number
  actionCount?: number
  hasFilterCode?: boolean
  hasDelay?: boolean
}
export const trackAppletConnectionCompleted = ({
  appletId,
  appletOwnerType,
  appletPublishedType,
  appletConfigType,
  appletAuthor,
  appletStatus,
  appletTier,
  triggerService,
  actionServices,
  appletTags,
  queryCount,
  actionCount,
  hasFilterCode,
  hasDelay,
}: TrackAppletConnectionCompletedPayload) => {
  trackEvent("Applet Connection Completed", {
    applet_slug: appletId,
    applet_owner_type: appletOwnerType,
    applet_published_type: appletPublishedType,
    applet_config_type: appletConfigType,
    applet_author: appletAuthor,
    applet_status: appletStatus,
    applet_tier: appletTier,
    trigger_service: triggerService,
    action_services: actionServices,
    applet_tags: appletTags,
    query_count: queryCount || 0,
    action_count: actionCount || 0,
    has_filter_code: hasFilterCode || false,
    has_delay: hasDelay || false,
  })
}

interface TrackAppletConfigurationStartedPayload {
  appletId: string
  appletOwnerType: AppletOwnerType
  configType: "initial" | "edit"
  hasExistingConfig: boolean
  appletTags: string[]
}
export const trackAppletConfigurationStarted = ({
  appletId,
  appletOwnerType,
  configType,
  hasExistingConfig,
  appletTags,
}: TrackAppletConfigurationStartedPayload) => {
  trackEvent("Applet Configuration Started", {
    applet_slug: appletId,
    applet_owner_type: appletOwnerType,
    config_type: configType,
    has_existing_config: hasExistingConfig,
    applet_tags: appletTags,
  })
}

interface TrackAppletConfigurationCompletedPayload extends TrackAppletConfigurationStartedPayload {
  fieldCount?: number
  timeToComplete?: number
}
export const trackAppletConfigurationCompleted = ({
  appletId,
  appletOwnerType,
  configType,
  hasExistingConfig,
  appletTags,
  fieldCount,
  timeToComplete,
}: TrackAppletConfigurationCompletedPayload) => {
  trackEvent("Applet Configuration Completed", {
    applet_slug: appletId,
    applet_owner_type: appletOwnerType,
    config_type: configType,
    has_existing_config: hasExistingConfig,
    applet_tags: appletTags,
    field_count: fieldCount || 0,
    time_to_complete: timeToComplete,
  })
}

// ============================================================================
// SERVICE CONNECTION EVENTS
// ============================================================================

interface TrackServiceConnectionStartedPayload {
  serviceName: string
  serviceModuleName: string
  authType: AuthType
  connectionContext: ConnectionContext
  isNewConnection: boolean
  inDiyFlow?: boolean
}
export const trackServiceConnectionStarted = ({
  serviceName,
  serviceModuleName,
  authType,
  connectionContext,
  isNewConnection,
  inDiyFlow,
}: TrackServiceConnectionStartedPayload) => {
  trackEvent("Service Connection Started", {
    service_name: serviceName,
    service_module_name: serviceModuleName,
    auth_type: authType, // "oauth" | "pin" | "token" | "modular"
    connection_context: connectionContext, // "trigger" | "action" | "query" | "manual"
    is_new_connection: isNewConnection,
    in_diy_flow: inDiyFlow || false,
  })
}

interface TrackServiceConnectionCompletedPayload extends TrackServiceConnectionStartedPayload {
  liveChannelId?: string
  timeToConnect?: number
}
export const trackConnectedService = ({
  serviceName,
  serviceModuleName,
  authType,
  connectionContext,
  isNewConnection,
  inDiyFlow,
  liveChannelId,
  timeToConnect,
}: TrackServiceConnectionCompletedPayload) => {
  trackEvent("Connected Service", {
    service_name: serviceName,
    service_module_name: serviceModuleName,
    auth_type: authType,
    connection_context: connectionContext,
    is_new_connection: isNewConnection,
    in_diy_flow: inDiyFlow || false,
    live_channel_id: liveChannelId,
    time_to_connect: timeToConnect,
  })
}

// ============================================================================
// SUBSCRIPTION EVENTS
// ============================================================================

// Helper to convert planId to subscription name
const toSubscriptionName = (planId: string): SubscriptionName => {
  switch (planId) {
    case "intermediate_pro":
      return "pro"
    case "pro":
      return "pro_plus"
    default:
      return planId
  }
}

// Helper to convert plan interval to pricing model
const toPricingModel = (interval: PlanInterval): SubscriptionPricingModel => {
  switch (interval) {
    case "month":
      return "monthly"
    case "year":
      return "annual"
  }
}

interface TrackSubscriptionPaymentStartedPayload {
  planId: string
  planInterval: PlanInterval
  currency: string
  eligibleForTrial?: boolean
  promoCode?: string | null
  paymentMethodType: PaymentMethodType
}
export const trackSubscriptionPaymentStarted = ({
  planId,
  planInterval,
  currency,
  eligibleForTrial,
  promoCode,
  paymentMethodType,
}: TrackSubscriptionPaymentStartedPayload) => {
  trackEvent("Subscription Payment Started", {
    subscription_name: toSubscriptionName(planId),
    subscription_pricing_model: toPricingModel(planInterval),
    currency,
    eligible_for_trial: eligibleForTrial || false,
    promo_code: promoCode || null,
    payment_method_type: paymentMethodType,
  })
}

interface TrackSubscriptionPaymentCompletedPayload {
  planId: string
  planInterval: PlanInterval
  currency: string
  promoCode?: string | null
  eligibleForTrial?: boolean
  paymentMethodType: PaymentMethodType
}
export const trackSubscriptionPaymentCompleted = ({
  planId,
  planInterval,
  currency,
  promoCode,
  eligibleForTrial,
  paymentMethodType,
}: TrackSubscriptionPaymentCompletedPayload) => {
  trackEvent("Subscription Payment Completed", {
    subscription_name: toSubscriptionName(planId),
    subscription_pricing_model: toPricingModel(planInterval),
    currency,
    promo_code: promoCode || null,
    eligible_for_trial: eligibleForTrial || false,
    payment_method_type: paymentMethodType,
  })
}

// ============================================================================
// PAYWALL EVENTS
// ============================================================================

type PaywallLocation =
  | "Applet Config"
  | "Applet Details"
  | "AI Applet Creator"
  | "Classic Applet Creator"
  | "Home"
  | "My Applets"
  | "Plans"
  | "Service"
  | "Connect"
  | "Settings"

type PaywallReason =
  | "Applet Features"
  | "Applet Quota"
  | "Rate Limit"
  | "Onboarding"
  | "Banner"
  | "Visit"

interface TrackSubscriptionPaywallShownPayload {
  location: PaywallLocation
  reason: PaywallReason
  targetTier?: string
}
export const trackSubscriptionPaywallShown = ({
  location,
  reason,
  targetTier,
}: TrackSubscriptionPaywallShownPayload) => {
  trackEvent("Subscription Paywall Shown", {
    location,
    reason,
    target_tier: targetTier,
  })
}
